import { Carousel, GridCarousel, Heading, ReviewCard } from '@design-system';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import SbEditable from 'storyblok-react';

import { HOME_PAGE_TYPE } from '../../constants';
import { AuthEntryPoint } from '../../features/auth/types';
import { pathFor } from '../../helpers/links';
import { HomeV2Data, HomeV2PageType } from '../../types/Home';
import { TrackCarouselSlide, TrackLinkClicked } from '../../utils/analytics';
import BannerV3 from '../BannerV3';
import SubscriptionBlock from '../Containers/SubscriptionBlock';
import ArtistVideosCarousel from '../Content/ArtistVideosCarousel';
import PageSection from '../Layout/PageSection';
import { LinkCardGroup } from '../LinkCards';
import { SupportFAQ } from '../Support/SupportFAQ/SupportFAQ';

type Props = {
  blok: HomeV2PageType;
  data: HomeV2Data;
};

const HomeV2: React.FC<Props> = ({
  blok,
  data: {
    artistCollaborationStatuses,
    draws,
    productArtistNames,
    productCountsByMedium,
    reviews,
    studioVisits,
  },
}) => {
  const { t } = useTranslation(['homev2']);

  const firstLinkCards = blok.linkCardGroup[0];
  const secondLinkCards = blok.linkCardGroup[1];

  const artistBanners = useMemo(() => {
    return blok.bannerArtists.map((banner, idx) => (
      <div key={banner._uid} className="col-span-12 md:col-span-6">
        <BannerV3
          analytics={{
            page: 'home',
            type: 'artist',
            position: idx,
          }}
          artistCollaborationStatuses={artistCollaborationStatuses}
          banner={banner}
          draws={draws}
          imageSizes={{
            sm: { ratio: '4/3', span: 12 },
            md: { ratio: '4/3', span: 6 },
            lg: { ratio: '4/3', span: 3 },
          }}
          layout="ARTIST"
          productArtistNames={productArtistNames}
        />
      </div>
    ));
  }, [blok.bannerArtists]);

  const heroBanners = useMemo(() => {
    return blok.bannerHero.slice(0, 1);
  }, [blok.bannerHero]);

  return (
    // @ts-expect-error require storyblok-react upgrade
    <SbEditable content={blok}>
      <main className="my-9 space-y-12 md:mb-12 md:mt-20 md:space-y-20">
        {/* Header */}
        <PageSection>
          <div className="space-y-6">
            <h1 className="mx-auto max-w-lg text-balance text-center text-md md:text-lg">
              {t('header.title')}
            </h1>
            <ul className="group flex flex-wrap justify-center max-md:flex-col max-md:divide-y max-md:divide-divider/20 md:items-center md:gap-3">
              <li className="max-md:py-3">
                <HeaderMediumLink
                  count={productCountsByMedium.prints}
                  href={pathFor({
                    page: 'WORKS_INDEX',
                    filters: { medium: 'Print editions' },
                  })}
                  title={t('header.mediums.prints')}
                />
              </li>
              <li className="max-md:py-3">
                <HeaderMediumLink
                  count={productCountsByMedium.sculptures}
                  href={pathFor({
                    page: 'WORKS_INDEX',
                    filters: { medium: 'Sculpture editions' },
                  })}
                  title={t('header.mediums.sculptures')}
                />
              </li>
              <li className="max-md:py-3">
                <HeaderMediumLink
                  count={productCountsByMedium.all}
                  href={pathFor({ page: 'WORKS_INDEX' })}
                  title={t('header.mediums.all')}
                />
              </li>
            </ul>
          </div>
        </PageSection>

        {/* Hero Banner */}
        {heroBanners.length > 0 && (
          <PageSection>
            {heroBanners.map((banner, idx) => (
              <BannerV3
                key={banner._uid}
                analytics={{
                  page: 'home',
                  type: 'hero',
                  position: idx,
                }}
                artistCollaborationStatuses={artistCollaborationStatuses}
                banner={banner}
                className="block"
                draws={draws}
                imageSizes={{
                  sm: { ratio: '3/4', span: 12 },
                  md: { ratio: '3/2', span: 12 },
                  lg: { ratio: '9/4', span: 12 },
                }}
                layout="HERO"
                productArtistNames={productArtistNames}
              />
            ))}
          </PageSection>
        )}

        {/* Link Cards - 1 */}
        {firstLinkCards && (
          <PageSection>
            <LinkCardGroup
              key={firstLinkCards._uid}
              analyticsPlacement={firstLinkCards.analyticsPlacement}
              cards={firstLinkCards.cards}
            />
          </PageSection>
        )}

        {/* Spotlight Banners */}
        <PageSection fullWidth>
          <div className="wrapper-website mb-4">
            <Heading level="h2">{t('banners.spotlight')}</Heading>
          </div>

          <GridCarousel
            gridCols={3}
            items={blok.bannerHighlights.map((banner, idx) => (
              <BannerV3
                key={banner._uid}
                analytics={{
                  page: 'home',
                  type: 'spotlight',
                  position: idx,
                }}
                artistCollaborationStatuses={artistCollaborationStatuses}
                banner={banner}
                draws={draws}
                imageSizes={{
                  sm: { ratio: '1/1', span: 12 },
                  md: {
                    ratio: idx === 1 || idx === 4 ? '3/4' : '1/1',
                    span: 6,
                  },
                  lg: {
                    ratio: idx === 1 || idx === 4 ? '3/4' : '1/1',
                    span: 4,
                  },
                }}
                layout="SPOTLIGHT"
                productArtistNames={productArtistNames}
              />
            ))}
          />
        </PageSection>

        {/* Artist Banners */}
        <PageSection fullWidth>
          <Carousel
            arrowPosition="middle"
            headline={t('banners.artists')}
            items={artistBanners}
            perPage={2}
          />
        </PageSection>

        {/* Studio Visits */}
        <PageSection fullWidth>
          <ArtistVideosCarousel
            analytics={{
              page: 'home',
              pageTitle: 'Home',
              context: 'studio visits',
            }}
            headline={t('studioVisits.title')}
            perPage={2}
            videos={studioVisits}
            withSeeAll
          />
        </PageSection>

        {/* Link Cards - 2 */}
        {secondLinkCards && (
          <PageSection>
            <LinkCardGroup
              key={secondLinkCards._uid}
              analyticsPlacement={secondLinkCards.analyticsPlacement}
              cards={secondLinkCards.cards}
            />
          </PageSection>
        )}

        {reviews.length > 0 && (
          <PageSection fullWidth>
            <Carousel
              headline={t('reviews.title')}
              items={reviews.map((review) => (
                <ReviewCard key={review.id} review={review} />
              ))}
              onSlide={(index) => {
                TrackCarouselSlide({
                  page: 'home',
                  context: 'reviews',
                  title: t('reviews.title'),
                  position: index,
                  carouselLength: reviews.length,
                });
              }}
              perPage={4}
            />
          </PageSection>
        )}

        {/* Sign up */}
        <PageSection>
          <SubscriptionBlock
            backgroundImage={blok.ctaImage}
            buttonLocation="homepage cta"
            description={t('signUp.description')}
            source={AuthEntryPoint.NewsletterHomepage}
            title={t('signUp.title')}
          />
        </PageSection>

        {blok.faq?.story?.content && (
          <PageSection>
            <SupportFAQ
              analytics={{
                page: 'home page',
                context: 'FAQ',
              }}
              faq={blok.faq?.story?.content}
              image={blok.faqImage}
              title={t('faq.title')}
            />
          </PageSection>
        )}
      </main>
    </SbEditable>
  );
};

export default HomeV2;

const HeaderMediumLink: React.FC<{
  title: string;
  href: string;
  count: number;
}> = ({ count, href, title }) => {
  return (
    <Link
      className="link flex items-start justify-center gap-2"
      href={href}
      onClick={() => {
        TrackLinkClicked({
          page: HOME_PAGE_TYPE,
          context: 'medium',
          title,
          url: href,
          isExternal: false,
        });
      }}
      prefetch={false}
    >
      <h2 className="text-center text-md font-bold md:text-lg">{title}</h2>
      <span className="text-center max-md:text-xs">{count}</span>
    </Link>
  );
};
